/* eslint-disable react/jsx-no-bind */
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography } from '@material-ui/core';
import CountyContext from '../../contexts/CountyContext';
import FiltersContext from '../../contexts/FiltersContext';
import StoryContext from '../../contexts/StoryContext';
import { displayChildAge, displayFacilityType, displayFamilyType } from '../../modules/data';
import { rdsTabulate } from '../../modules/rds';
import { stateFIPS } from '../../modules/states';
import { toFormattedNumber } from '../../modules/text';
import ResponsiveContainer from '../ResponsiveContainer';
import StateCountyMap from '../StateCountyMap';

function CountyMapNSH() {
  const { stateCode } = useParams();
  const {
    primaryColor, lightColor, darkColor, selectionColor, hoverColor,
  } = useContext(StoryContext);
  const {
    county, setCounty, setCountyCode, countyCode,
  } = useContext(CountyContext);
  const [, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);

  const { familyType, settingType, childAge } = useContext(FiltersContext);

  useEffect(() => {
    function getProvidersSubsidized() {
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND license_type=LIC AND operating_during_nsh=1`;

      if (settingType === 'All') {
        whereClause += ' AND (setting_type=HOM OR setting_type=CEN)';
      } else if (settingType === 'Center-based child care') {
        whereClause += ' AND setting_type=CEN';
      } else {
        whereClause += ' AND setting_type=HOM';
      }

      return rdsTabulate('ccdc', 'provider', {
        dims: 'county_fips',
        measure: 'capacity:COUNT(provider_id)',
        where: whereClause,
        format: 'amcharts',
        inject: false,
      }, true);
    }

    function getProviders() {
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND license_type=LIC`;

      if (settingType === 'All') {
        whereClause += ' AND (setting_type=HOM OR setting_type=CEN)';
      } else if (settingType === 'Center-based child care') {
        whereClause += ' AND setting_type=CEN';
      } else {
        whereClause += ' AND setting_type=HOM';
      }

      return rdsTabulate('ccdc', 'provider', {
        dims: 'county_fips',
        measure: 'capacity:COUNT(provider_id)',
        where: whereClause,
        format: 'amcharts',
        inject: false,
      }, true);
    }

    Promise.all([getProviders(), getProvidersSubsidized()])
      .then((results) => {
        const providers = results[0].parsedBody.dataProvider;
        const providersSubsidized = results[1].parsedBody.dataProvider;
        const providersCount = providers.reduce((a, v) => (a + Number.parseInt(v.capacity, 10)), 0);
        const providersNSHCount = providersSubsidized.reduce((a, v) => (a + Number.parseInt(v.capacity, 10)), 0);

        const payload = providersSubsidized.map((v) => {
          const totals = _.find(providers, { county_fips: v.county_fips }); // find subsidized numbers for the county
          // Object.assign(v, { value: ((v.capacity / providersCount)), providersCount });
          const result = { ...v };
          result.total = totals.capacity;
          result.value = result.capacity / result.total;
          result.providersCount = providersCount;
          result.providersNSHCount = providersNSHCount;
          return result;
        });
        setData(payload);
      })
      .catch((rdsError) => {
        setError(rdsError);
      })
      .finally(() => {
        setReady(true);
      });
  }, [stateCode, settingType]);

  const toolTipFormatterFunction = function toolTipFormatterFunction(d, dataPoint) {
    if (!dataPoint) {
      return 'No data';
    }

    return `In <b>${d.properties.name} ${d.properties.TYPE}, ${d.properties.STATE}</b> the total number of providers who offer NSH care is <b>${dataPoint.total}</b>. That's <b>${toFormattedNumber(dataPoint.value * 100)}</b> of every 100 providers. 
    <br />
    (Overall in <b>${d.properties.STATE}</b>, <b>${toFormattedNumber((dataPoint.providersNSHCount / dataPoint.providersCount) * 100)}</b> of every 100 providers offer NSH care).`;
  };

  const legendValueFormatter = function legendValueFormatter(v) {
    return Number.parseInt(v, 10);
  };

  const legendLabelFormatter = function legendLabelFormatter(v) {
    return toFormattedNumber(v * 100);
  }

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <ResponsiveContainer aspect={1.5} width="100%">
        <StateCountyMap
          data={data}
          stateCode={stateCode}
          legendValueFormatter={legendValueFormatter}
          legendLabelFormatter={legendLabelFormatter}
          legendTitleText={`The darker the color, the greater the number of ${displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')} providers who offer NSH care per 100 providers.`}
          colorHigh={darkColor}
          colorLow={lightColor}
          primaryColor={primaryColor}
          selectionColor={selectionColor}
          hoverColor={hoverColor}
          setCounty={setCounty}
          setCountyCode={setCountyCode}
          county={county}
          countyCode={countyCode}
          toolTipFormatterFunction={toolTipFormatterFunction}
          familyType={familyType}
          settingType={settingType}
          childAge={childAge}
          displayFacilityType={displayFacilityType}
          displayFamilyType={displayFamilyType}
          displayChildAge={displayChildAge}
        />
      </ResponsiveContainer>
    </>
  );
}

export default CountyMapNSH;

